import type { GetCurrentUserQuery } from './use-current-user.generated'

import { useStorage } from '@vueuse/core'
import { GetCurrentUserDocument } from './use-current-user.generated'

export type CurrentUser = NonNullable<GetCurrentUserQuery['user']>

const user = ref<CurrentUser>()
const canChangeOrgConfig = ref<boolean>(false)
const canChangeOrg = ref<boolean>(false)
const canManageAssets = ref<boolean>(false)
const canCreateAssets = ref<boolean>(false)
const activeSkillProfile = ref<CurrentUser['activeSkillProfile']>()
// We set this manually because `isTrue` from `useQuery` apparently doesn't
// work with manually triggered fetches through `execute`
const isDone = ref<boolean>(true)

const isBeingImpersonated = useStorage('isBeingImpersonated', false)

const lastSentryUserId = ref<string>()

export default function () {
  const posthog = useNuxtApp().$posthog
  const sentrySetUser = useNuxtApp().$sentrySetUser
  const sentrySetContext = useNuxtApp().$sentrySetContext

  const {
    onData,
    execute: fetchCurrentUser,
    isFetching,
  } = useQuery(
    {
      query: GetCurrentUserDocument,
      tags: ['user'],
      fetchOnMount: false,
    },
    ({ data }) => data?.user,
  )

  onData((data) => {
    if (!data.user) return

    user.value = data.user
    canChangeOrg.value = data.user.permissions.canChangeOrg
    canChangeOrgConfig.value = data.user.permissions.canChangeOrgConfig
    canManageAssets.value = data.user.permissions.canManageAssets
    canCreateAssets.value = data.user.permissions.canCreateAssets
    isBeingImpersonated.value = data.user.isBeingImpersonated
    activeSkillProfile.value = data.user.activeSkillProfile

    // Identify whenever the client isn't already identified as this user — this
    // covers the first load, and re-login after logout's posthog.reset().
    const ph = posthog()
    if (
      ph &&
      !data.user.isBeingImpersonated &&
      data.user.analyticsId &&
      ph.get_distinct_id() !== data.user.analyticsId
    ) {
      // One-time per-user Segment→PostHog person merge marker; remove with the alias call.
      const posthogAliasMigrated = useStorage(`posthog-alias-migrated:${data.user.id}`, false)
      if (!posthogAliasMigrated.value) {
        // Merge the pre-migration GID-keyed person into the new analyticsId
        // person; remove after one release.
        ph.alias(data.user.analyticsId, data.user.id)
        posthogAliasMigrated.value = true
      }
      ph.identify(data.user.analyticsId, {
        name: data.user.display,
        email: data.user.email,
        organization: data.user.organizations[0].slug,
      })
    }

    if (lastSentryUserId.value !== data.user.id) {
      lastSentryUserId.value = data.user.id
      sentrySetUser({
        id: data.user.id,
        email: data.user.email,
      })
      sentrySetContext('userDetails', {
        isBeingImpersonated: data.user.isBeingImpersonated,
      })
    }

    isDone.value = true
  })

  return {
    user,
    canChangeOrg,
    canChangeOrgConfig,
    canManageAssets,
    canCreateAssets,
    fetchCurrentUser,
    isBeingImpersonated,
    isFetching,
    isDone,
    activeSkillProfile,
  }
}
